// Web storage throws instead of degrading: quota exceeded on write, and any access at all when the
// browser blocks storage (Safari private mode, disabled site data). Never let that reach a render.

// The store is resolved per call, not once up front - where site data is blocked the bare
// `localStorage` property access throws too, and at module scope there is no caller left to catch it.
function createSafeStorage(getStorage: () => Storage) {
  return {
    getItem(key: string): Nullable<string> {
      try {
        return getStorage().getItem(key)
      } catch {
        return null
      }
    },

    /** @returns whether the value was actually persisted */
    setItem(key: string, value: string): boolean {
      try {
        getStorage().setItem(key, value)
        return true
      } catch {
        return false
      }
    },

    removeItem(key: string) {
      try {
        getStorage().removeItem(key)
      } catch {
        // storage unavailable: nothing to remove
      }
    },

    removeItemsByPrefix(prefix: string) {
      try {
        const storage = getStorage()
        // Backwards, because removing a key only shifts the indices above it.
        for (let index = storage.length - 1; index >= 0; index--) {
          const key = storage.key(index)
          if (key?.startsWith(prefix)) {
            storage.removeItem(key)
          }
        }
      } catch {
        // storage unavailable: nothing to remove
      }
    },
  }
}

export const {
  getItem: safeGetLocalItem,
  removeItem: safeRemoveLocalItem,
  removeItemsByPrefix: safeRemoveLocalItemsByPrefix,
  setItem: safeSetLocalItem,
} = createSafeStorage(() => localStorage)

/** Survives a reload, dropped when the tab closes. */
export const {
  getItem: safeGetSessionItem,
  removeItem: safeRemoveSessionItem,
  removeItemsByPrefix: safeRemoveSessionItemsByPrefix,
  setItem: safeSetSessionItem,
} = createSafeStorage(() => sessionStorage)
